import {
  Box,
  Button,
  Typography,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
  ToggleButton,
  ToggleButtonGroup
} from "@mui/material";
import {
  TableChart as TableChartIcon,
  ShowChart as ShowChartIcon,
  FileDownloadOutlined
} from "@mui/icons-material";
import {
  GridToolbarColumnsButton,
  GridToolbarContainer,
  GridToolbarDensitySelector,
  GridToolbarFilterButton,
} from "@mui/x-data-grid-pro";
import * as XLSX from "xlsx";
import { COUNTRY_CODE_MAP } from "../../../../constants/constant";

const ReportToolbar = ({
  viewMode,
  setViewMode,
  filters,
  appliedFilters,
  handleFilterChange,
  onOpenFilter,
  volumeOptions,
  currency,
  showGridToolbar = false,
  reportData = [],
  columns = []
}) => {

  const exportValueFormatter = (value) => {
    if (!value || value === null || value === "" || value === "N/A") return "-";
    else return Number(value).toFixed(2).replace(".", ",");
  };

  const handleExportXLSX = () => {
    if (!reportData || reportData.length === 0) {
      alert("No data available to export");
      return;
    }

    // Create export headers from columns
    const exportHeaders = columns.map(col => ({
      label: col.headerName || col.field,
      key: col.field
    }));

    // Create export rows from report data
    const exportRows = reportData.map((row) =>
      exportHeaders.reduce((acc, col) => {
        const value = row[col.key];
        if (typeof value === 'number' && col.key !== 'id') {
          acc[col.label] = exportValueFormatter(value);
        } else {
          acc[col.label] = value || "-";
        }
        return acc;
      }, {})
    );

    // Create worksheet and workbook
    const ws = XLSX.utils.json_to_sheet(exportRows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Monthly Extension Report");
    
    // Generate filename with country and date range
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                       'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    
    const countryName = appliedFilters?.country ? (COUNTRY_CODE_MAP[appliedFilters.country] || appliedFilters.country) : 'All';
    const startMonthName = appliedFilters?.startMonth ? monthNames[appliedFilters.startMonth - 1] : 'Jan';
    const endMonthName = appliedFilters?.endMonth ? monthNames[appliedFilters.endMonth - 1] : 'Dec';
    const startYear = appliedFilters?.startYear || new Date().getFullYear();
    const endYear = appliedFilters?.endYear || new Date().getFullYear();
    
    const filename = `MonthlyExtensionReport_${countryName}_${startMonthName}-${startYear}_${endMonthName}-${endYear}.xlsx`;
    
    // Download the file
    XLSX.writeFile(wb, filename);
  };

  return (
    <Box sx={{ 
      display: 'flex', 
      justifyContent: 'space-between', 
      alignItems: 'center', 
      p: 1,
      borderBottom: viewMode === 'chart' ? '1px solid #e0e0e0' : 'none'
    }}>
      {showGridToolbar ? (
        <GridToolbarContainer>
          <GridToolbarColumnsButton />
          <GridToolbarFilterButton />
          <GridToolbarDensitySelector />
          <Button
            color="inherit"
            size="small"
            startIcon={<FileDownloadOutlined style={{ color: "green" }} />}
            style={{
              boxShadow: "none",
              border: "none",
              background: "none",
              color: "green",
            }}
            onClick={handleExportXLSX}
          >
            Export
          </Button>
        </GridToolbarContainer>
      ) : (
        <Typography variant="h6">Monthly Extension Report</Typography>
      )}
      
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
        {/* Country Code Display */}
        {appliedFilters?.country && (
          <Typography variant="body2" sx={{ 
            color: 'text.secondary', 
            fontWeight: 'medium',
            fontSize: '0.875rem' 
          }}>
            {COUNTRY_CODE_MAP[appliedFilters.country] || appliedFilters.country}
          </Typography>
        )}
        
        {/* Volume Filter */}
        <FormControl component="fieldset" sx={{ minWidth: 120 }}>
          <RadioGroup
            value={filters.volume}
            onChange={(e) => handleFilterChange('volume', e.target.value)}
            row
            sx={{ gap: 1 }}
          >
            {volumeOptions.map((option) => (
              <FormControlLabel
                key={option.value}
                value={option.value}
                control={
                  <Radio 
                    size="small" 
                    sx={{
                      color: '#666',
                      '&.Mui-checked': {
                        color: '#367c2b',
                        '& .MuiSvgIcon-root': {
                          fontSize: 20,
                          // Slightly bigger green dot in center with green border
                          background: 'radial-gradient(circle, #367c2b 35%, transparent 35%)',
                          borderRadius: '50%',
                          border: '2px solid #367c2b',
                        },
                      },
                      '& .MuiSvgIcon-root': {
                        fontSize: 20,
                      },
                    }}
                  />
                }
                label={option.value === 'value' && currency ? `${option.label} (${currency})` : option.label}
                sx={{ 
                  '& .MuiFormControlLabel-label': { 
                    fontSize: '0.875rem',
                    fontWeight: filters.volume === option.value ? 600 : 400,
                    color: filters.volume === option.value ? '#367c2b' : '#666',
                  },
                  margin: '0 8px',
                }}
              />
            ))}
          </RadioGroup>
        </FormControl>

        <ToggleButtonGroup
          value={viewMode}
          exclusive
          onChange={(event, newViewMode) => {
            if (newViewMode !== null) {
              setViewMode(newViewMode);
            }
          }}
          aria-label="view mode"
          size="small"
          sx={{
            '& .MuiToggleButton-root': {
              border: '1px solid #e0e0e0',
              color: '#666',
              backgroundColor: 'white',
              padding: '8px 12px',
              minWidth: '44px',
              '&:hover': {
                backgroundColor: '#f5f5f5',
                borderColor: '#bdbdbd',
                color: '#333',
              },
              '&.Mui-selected': {
                backgroundColor: '#f5f5f5',
                color: '#367c2b',
                borderColor: '#367c2b',
                fontWeight: 600,
                '&:hover': {
                  backgroundColor: '#eeeeee',
                  borderColor: '#2d6323',
                  color: '#2d6323',
                },
              },
              '& .MuiSvgIcon-root': {
                fontSize: '1.2rem',
              },
            },
          }}
        >
          <ToggleButton value="chart" aria-label="chart view">
            <ShowChartIcon />
          </ToggleButton>
          <ToggleButton value="table" aria-label="table view">
            <TableChartIcon />
          </ToggleButton>
        </ToggleButtonGroup>
        
        <Button
          variant="outlined"
          onClick={onOpenFilter}
          sx={{ ml: 1 }}
        >
          Advanced Filters
        </Button>
      </Box>
    </Box>
  );
};

export default ReportToolbar;
